import { type HTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/cn';

/* --------------------------------------------------------------------------
   Badge variants
   -------------------------------------------------------------------------- */

const badgeVariants = cva(
  [
    'inline-flex items-center gap-1 font-[family-name:var(--font-heading)] font-semibold',
    'rounded-[var(--radius-full)] select-none',
    'transition-colors duration-[var(--duration-fast)]',
  ].join(' '),
  {
    variants: {
      status: {
        positive: 'bg-status-positive-light text-status-positive',
        negative: 'bg-status-negative-light text-status-negative',
        neutral: 'bg-status-neutral-light text-status-neutral',
        info: 'bg-status-info-light text-status-info',
      },
      size: {
        sm: 'px-2 py-0.5 text-xs',
        md: 'px-3 py-1 text-sm',
      },
    },
    defaultVariants: {
      status: 'neutral',
      size: 'md',
    },
  },
);

/* --------------------------------------------------------------------------
   Types
   -------------------------------------------------------------------------- */

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  /** Optional leading dot indicator. */
  dot?: boolean;
}

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

export function Badge({
  className,
  status,
  size,
  dot = false,
  children,
  ...props
}: BadgeProps) {
  return (
    <span
      className={cn(badgeVariants({ status, size }), className)}
      {...props}
    >
      {dot && (
        <span
          className={cn(
            'h-1.5 w-1.5 rounded-full',
            status === 'positive' && 'bg-status-positive',
            status === 'negative' && 'bg-status-negative',
            status === 'neutral' && 'bg-status-neutral',
            status === 'info' && 'bg-status-info',
            !status && 'bg-status-neutral',
          )}
          aria-hidden
        />
      )}
      {children}
    </span>
  );
}

export { badgeVariants };
